import { conditionActionFilter, ConditionActionType, ConditionRule, ConfiguratorConditionGroup, ConfiguratorId, ConfiguratorOption, OperatorNoteInput, OptionCondition, OptionGroupId, OptionId } from "#models/Configurator"
import * as Struct from "effect/Struct"
import { S, TaggedRequestFor } from "../../lib.ts"
import { ConfiguratorPriceTableEntryView } from "./Views.ts"

// codegen:start {preset: meta, sourcePrefix: src/resources/ }
const Req = TaggedRequestFor("Admin/Configurator/ConditionGroup")
// codegen:end

export class ConfiguratorConditionGroupListEntry
  extends S.Opaque<ConfiguratorConditionGroupListEntry, ConfiguratorConditionGroupListEntry.Encoded>()(
    S
      .Struct({
        ...Struct.pick(ConfiguratorConditionGroup.fields, ["id", "title", "isEnabled", "note"]),
        optionIds: S.Array(OptionId),
        actionTypes: S.Array(ConditionActionType),
        // option group ids, or RECAP_GROUP_TARGET for the "no active group" state
        groupTargets: S.Array(S.String),
        // what it points at that this configurator lacks, e.g. after a copy
        missing: S.Array(S.String)
      })
      .annotate({ identifier: "ConfiguratorConditionGroupListEntry" })
  )
{}

export class AllConfiguratorConditionGroups
  extends Req.Query<AllConfiguratorConditionGroups>()("AllConfiguratorConditionGroups", {
    id: ConfiguratorId
  }, {
    success: S.Struct({
      conditionGroups: S.Array(ConfiguratorConditionGroupListEntry),
      options: S.Array(
        S.Struct({ id: OptionId, name: S.NonEmptyString255, groupId: OptionGroupId, groupName: S.NonEmptyString255 })
      ),
      optionGroups: S.Array(S.Struct({ id: OptionGroupId, name: S.NonEmptyString255 }))
    })
  })
{}
export class CloneConfiguratorConditionGroup
  extends Req.Command<CloneConfiguratorConditionGroup>()("CloneConfiguratorConditionGroup", {
    configuratorId: ConfiguratorId,
    id: S.StringId
  }, {
    success: S.Struct({
      id: S.StringId
    })
  })
{}
// The Bedingungen of another configurator, for "Bedingungen kopieren": each with
// the references the target lacks (labels like "Option ‹Breite (breite)›").
export class ConditionGroupsToCopy extends Req.Query<ConditionGroupsToCopy>()("ConditionGroupsToCopy", {
  sourceId: ConfiguratorId,
  targetId: ConfiguratorId
}, {
  success: S.Struct({
    conditionGroups: S.Array(S.Struct({
      id: S.StringId,
      title: S.NonEmptyString255,
      isEnabled: S.Boolean,
      missing: S.Array(S.String)
    }))
  })
}) {}
// Appends disabled copies of the chosen Bedingungen of `sourceId` to `targetId`,
// with their references looked up again in the target.
export class CopyConfiguratorConditionGroups
  extends Req.Command<CopyConfiguratorConditionGroups>()("CopyConfiguratorConditionGroups", {
    sourceId: ConfiguratorId,
    targetId: ConfiguratorId,
    ids: S.Array(S.StringId)
  }, {
    success: S.Struct({
      copied: S.NonNegativeInt,
      withMissing: S.NonNegativeInt
    })
  })
{}
export class CreateConfiguratorConditionGroup
  extends Req.Command<CreateConfiguratorConditionGroup>()("CreateConfiguratorConditionGroup", {
    configuratorId: ConfiguratorId,
    title: S.NonEmptyString255
  }, {
    success: S.Struct({
      id: S.StringId
    })
  })
{}
export class GetConfiguratorConditionGroupResponse extends S.Opaque<GetConfiguratorConditionGroupResponse>()(
  S
    .Struct({
      conditionGroup: ConfiguratorConditionGroup,
      priceTables: S.Array(ConfiguratorPriceTableEntryView),
      options: S.Array(ConfiguratorOption),
      optionGroups: S.Array(S.Struct({ id: OptionGroupId, name: S.NonEmptyString255 }))
    })
    .annotate({ identifier: "GetConfiguratorConditionGroupResponse" })
) {}

export class GetConfiguratorConditionGroup
  extends Req.Query<GetConfiguratorConditionGroup>()("GetConfiguratorConditionGroup", {
    id: S.StringId,
    configuratorId: ConfiguratorId
  }, {
    success: GetConfiguratorConditionGroupResponse
  })
{}

export class RemoveConfiguratorConditionGroup
  extends Req.Command<RemoveConfiguratorConditionGroup>()("RemoveConfiguratorConditionGroup", {
    configuratorId: ConfiguratorId,
    id: S.StringId
  }, {
    // what RestoreConfiguratorConditionGroup needs to undo it
    success: S.Struct({
      conditionGroup: ConfiguratorConditionGroup,
      index: S.NonNegativeInt
    })
  })
{}

export class RestoreConfiguratorConditionGroup
  extends Req.Command<RestoreConfiguratorConditionGroup>()("RestoreConfiguratorConditionGroup", {
    configuratorId: ConfiguratorId,
    conditionGroup: ConfiguratorConditionGroup,
    index: S.NonNegativeInt
  })
{}

// Writes only the note, so the list can save it without the rest of the group.
export class SetConfiguratorConditionGroupNote
  extends Req.Command<SetConfiguratorConditionGroupNote>()("SetConfiguratorConditionGroupNote", {
    configuratorId: ConfiguratorId,
    id: S.StringId,
    note: OperatorNoteInput
  })
{}

export class SortConfiguratorConditionGroup
  extends Req.Command<SortConfiguratorConditionGroup>()("SortConfiguratorConditionGroup", {
    configuratorId: ConfiguratorId,
    order: S.Array(S.StringId)
  })
{}
export const OptionConditionActionInput = S // S.Struct(ConditionAction.omit("timestamps")) // doesn't work because of the embedded Struct+filter
  .Struct(
    {
      id: S.StringId.withConstructorDefault,
      optionId: OptionId,
      type: ConditionActionType,
      typeValue: S.NullOr(S.NonEmptyString255).withConstructorDefault // TODO: possibly good to combine into a type with `type` so the value can be constrained?
    }
  )
  .pipe(S.check(conditionActionFilter))

export const OptionConditionRuleInput = ConditionRule.mapFields(Struct.omit(["timestamps"]))

export class OptionConditionInput extends S.Opaque<OptionConditionInput>()(
  S
    .Struct({
      ...Struct.omit(OptionCondition.fields, ["timestamps"]),
      actions: S.Array(OptionConditionActionInput),
      rules: S.Array(OptionConditionRuleInput)
    })
    .annotate({ identifier: "OptionConditionInput" })
) {}

export class ConfiguratorConditionGroupInput extends S.Opaque<ConfiguratorConditionGroupInput>()(
  S
    .Struct({
      ...Struct.omit(ConfiguratorConditionGroup.fields, ["timestamps", "id", "conditions"]),
      note: OperatorNoteInput,
      conditions: S.Array(OptionConditionInput)
    })
    .annotate({ identifier: "ConfiguratorConditionGroupInput" })
) {}

export class UpdateConfiguratorConditionGroup
  extends Req.Command<UpdateConfiguratorConditionGroup>()("UpdateConfiguratorConditionGroup", {
    configuratorId: ConfiguratorId,
    id: S.StringId,
    conditionGroup: ConfiguratorConditionGroupInput
  })
{}

// codegen:start {preset: model}
//
export namespace ConfiguratorConditionGroupListEntry {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorConditionGroupListEntry> {}
}
//
// codegen:end
//
